import {
  CUBE_AGGREGATE_INTERACTION,
  CUBE_AGGREGATE_INTERACTION_EMPLOYEE,
  CUBE_AGGREGATE_INTERACTION_FIELD_FORCE,
  CUBE_AGGREGATE_INTERACTION_PRODUCT,
  CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE,
  CUBE_WEBSITE_AGGREGATE_INTERACTION,
} from '@/api/hcp/interaction/types';
import FiltersContext, { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { AdditionalChannelTabs, Channel } from '@/models';
import { therapeuticIndicationDimensions } from '@/models/filter/utils/hierarchicalFilter';
import { buildInteractionIndicationFilter } from '@/models/filter/configs/channelSpecific/interactionIndicationFilter';
import { FilterType } from '@/models/filter/FilterType';
import { useLocalOptionPaths } from '@/models/filter/utils/localOptionPathsStore';
import parseHierarchicalFilterOldTree from '@/models/filter/utils/parseHierarchicalFilterOldTree';
import { Period } from '@/types/date';
import { isPeriod } from '@/utils/date/isPeriod';
import { LogicalAndFilter, TimeDimension, TimeDimensionComparison } from '@cubejs-client/core';
import { useContext, useMemo } from 'react';
import { formatDateTimeEndOfDayDropTZ, formatDateTimeStartOfDayDropTZ } from '../date';

export interface AdditionalFilterState {
  oneToOne?: string[];
  promo?: string[];
  productMessageGroup?: string[];
  productMessageName?: string[];
  eventType?: string[];
  eventName?: string[];
  interactionType?: string[];
  products?: string[];
  indications?: string[];
  interactionIndication?: string[];
  emailCampaign?: string[];
  emailChannel?: string[];
  emailTopic?: string[];
  websitePortal?: string[];
  fieldForce?: string[];
  type?: string[];
  interactionTypeAllChannels?: string[];
  productLocal?: string[];
  rocheEmployee?: string[];
  localSource?: string[];
}

const DATE_RANGE_FORMAT = 'YYYY-MM-DD';

const useGetDateRangeTimeDimensionComparison = (dimension: string): TimeDimensionComparison => {
  const {
    filterState: { dateRange, comparisonPeriod },
  } = useContext(FiltersContext);

  const { beginn: beginnCurrent, end: endCurrent } = dateRange as Period;
  const { beginn: beginnPrevious, end: endPrevious } = comparisonPeriod as Period;

  return {
    dimension,
    compareDateRange: [
      [formatDateTimeStartOfDayDropTZ(beginnCurrent), formatDateTimeEndOfDayDropTZ(endCurrent)],
      [formatDateTimeStartOfDayDropTZ(beginnPrevious), formatDateTimeEndOfDayDropTZ(endPrevious)],
    ],
  };
};

const useGetDateRangeTimeDimension = (dimension: string): TimeDimension => {
  const {
    filterState: { dateRange },
  } = useFiltersContext();
  const dateRangeStart = isPeriod(dateRange) ? dateRange.beginn.format(DATE_RANGE_FORMAT) : undefined;
  const dateRangeEnd = isPeriod(dateRange) ? dateRange.end.format(DATE_RANGE_FORMAT) : undefined;

  return useMemo<TimeDimension>(() => {
    if (dateRangeStart === undefined || dateRangeEnd === undefined) {
      return { dimension };
    }

    return {
      dimension,
      dateRange: [dateRangeStart, dateRangeEnd],
    };
  }, [dateRangeEnd, dateRangeStart, dimension]);
};

const useGetAdditionalFilter = (
  state: AdditionalFilterState,
  channel?: Channel | AdditionalChannelTabs,
): LogicalAndFilter => {
  if (!channel) channel = Channel.VISITS_1_TO_1;
  const indicationOptionPaths = useLocalOptionPaths(FilterType.INTERACTION_INDICATION);
  return useMemo(() => {
    const appendInteractionIndicationFilter = (targetFilter: LogicalAndFilter) => {
      if (!state?.interactionIndication?.length) return;

      const indicationFilter = buildInteractionIndicationFilter(
        state.interactionIndication,
        therapeuticIndicationDimensions(CUBE_AGGREGATE_INTERACTION_PRODUCT),
        undefined,
        indicationOptionPaths,
      );

      if (indicationFilter) {
        targetFilter.and.push(indicationFilter);
      }
    };

    const getEventFilters = (): LogicalAndFilter => {
      const eventFilters: LogicalAndFilter = {
        and: [],
      };

      if (state?.eventType?.length) {
        eventFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.activity_type`,
          operator: 'equals',
          values: [...state.eventType],
        });
      }
      if (state?.eventName?.length) {
        eventFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.event_name`,
          operator: 'equals',
          values: [...state.eventName],
        });
      }
      if (state?.fieldForce?.length) {
        eventFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
          operator: 'equals',
          values: [...state.fieldForce],
        });
      }
      if (state?.localSource?.length) {
        eventFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.event_source_country`,
          operator: 'equals',
          values: [...state.localSource],
        });
      }
      if (state?.rocheEmployee?.length) {
        eventFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
          operator: 'equals',
          values: [...state.rocheEmployee],
        });
      }
      return eventFilters;
    };

    const getEmailFilters = (): LogicalAndFilter => {
      const emailFilters: LogicalAndFilter = {
        and: [],
      };

      if (state?.emailCampaign?.length) {
        emailFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.email_name`,
          operator: 'equals',
          values: [...state.emailCampaign],
        });
      }
      if (state?.productLocal?.length) {
        emailFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
          operator: 'equals',
          values: [...state.productLocal],
        });
      }
      appendInteractionIndicationFilter(emailFilters);
      if (state?.emailTopic?.length) {
        emailFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.activity_type`,
          operator: 'equals',
          values: [...state.emailTopic],
        });
      }
      if (state?.emailChannel?.length) {
        emailFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.email_diffusion`,
          operator: 'equals',
          values: [...state.emailChannel],
        });
      }

      return emailFilters;
    };

    const get1To1VisitsFilters = (): LogicalAndFilter => {
      const channelFilters: LogicalAndFilter = {
        and: [],
      };

      if (state?.rocheEmployee?.length) {
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
          operator: 'equals',
          values: [...state.rocheEmployee],
        });
      }

      if (state?.oneToOne?.length) {
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_2`,
          operator: 'equals',
          values: [...state.oneToOne],
        });
      }
      if (state?.promo?.length) {
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION}.activity_type`,
          operator: 'equals',
          values: [...state.promo],
        });
      }
      if (state?.productMessageGroup?.length) {
        channelFilters.and.push({
          member: `${CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE}.product_message_group`,
          operator: 'equals',
          values: [...state.productMessageGroup],
        });
      }
      if (state?.productMessageName?.length) {
        channelFilters.and.push({
          member: `${CUBE_INTERACTION_AGGREGATE_PRODUCT_MESSAGE}.product_message_name`,
          operator: 'equals',
          values: [...state.productMessageName],
        });
      }

      if (state?.type?.length) {
        const interactionValues = [];

        if (state.type.includes('Global')) {
          interactionValues.push('GMCL');
        }

        if (state.type.includes('Local')) {
          interactionValues.push('OCE-P', 'Unite');
        }

        if (interactionValues.length) {
          channelFilters.and.push({
            member: `${CUBE_AGGREGATE_INTERACTION}._tech_src_sys`,
            operator: 'equals',
            values: interactionValues,
          });
        }
      }
      if (state?.productLocal?.length) {
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
          operator: 'equals',
          values: [...state.productLocal],
        });
      }
      appendInteractionIndicationFilter(channelFilters);
      if (state?.fieldForce?.length) {
        channelFilters.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
          operator: 'equals',
          values: [...state.fieldForce],
        });
      }
      return channelFilters;
    };
    const getWebsiteFilter = (): LogicalAndFilter => {
      const websiteFilter: LogicalAndFilter = {
        and: [],
      };

      if (state?.productLocal?.length) {
        websiteFilter.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
          operator: 'equals',
          values: [...state.productLocal],
        });
      }
      appendInteractionIndicationFilter(websiteFilter);
      if (state?.websitePortal?.length) {
        websiteFilter.and.push({
          member: `${CUBE_WEBSITE_AGGREGATE_INTERACTION}.website_name`,
          operator: 'equals',
          values: [...state.websitePortal],
        });
      }

      return websiteFilter;
    };
    const getAllChannelsFilter = () => {
      const allChannelsFilter: LogicalAndFilter = {
        and: [],
      };
      if (state?.interactionTypeAllChannels?.length) {
        const dimensions = [
          `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_1`,
          `${CUBE_AGGREGATE_INTERACTION}.interaction_type_lvl_2`,
        ];
        const parsedInteractionFilter = parseHierarchicalFilterOldTree(state?.interactionTypeAllChannels, dimensions);
        allChannelsFilter.and.push(parsedInteractionFilter);
      }

      if (state?.fieldForce?.length) {
        allChannelsFilter.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_FIELD_FORCE}.field_force_code`,
          operator: 'equals',
          values: [...state.fieldForce],
        });
      }

      appendInteractionIndicationFilter(allChannelsFilter);
      if (state?.productLocal?.length) {
        allChannelsFilter.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_PRODUCT}.product`,
          operator: 'equals',
          values: [...state.productLocal],
        });
      }
      if (state?.rocheEmployee?.length) {
        allChannelsFilter.and.push({
          member: `${CUBE_AGGREGATE_INTERACTION_EMPLOYEE}.employee_name`,
          operator: 'equals',
          values: [...state.rocheEmployee],
        });
      }
      return allChannelsFilter;
    };
    const globalFilters: LogicalAndFilter = {
      and: [],
    };

    if (channel === Channel.VISITS_1_TO_1) {
      const channelFilters = get1To1VisitsFilters();
      globalFilters.and.push(...channelFilters.and);
    } else if (channel === Channel.EVENT) {
      const eventFilters = getEventFilters();
      globalFilters.and.push(...eventFilters.and);
    } else if (channel === Channel.EMAIL) {
      const emailFilters = getEmailFilters();
      globalFilters.and.push(...emailFilters.and);
    } else if (channel === Channel.WEBSITE) {
      const websiteFilters = getWebsiteFilter();
      globalFilters.and.push(...websiteFilters.and);
    } else if (channel === AdditionalChannelTabs.ALL_CHANNEL) {
      const allChannelsFilter = getAllChannelsFilter() || {
        and: [],
      };
      globalFilters.and.push(...allChannelsFilter.and);
    }

    return globalFilters;
  }, [channel, state, indicationOptionPaths]);
};

export { useGetAdditionalFilter, useGetDateRangeTimeDimension, useGetDateRangeTimeDimensionComparison };
